/**
 * Race time: the wall-clock time since `startAt` on the synchronized server clock. It ranks
 * the race, so it never stops: not for deaths, not for a hidden tab, not for boosts. A race
 * against the clock ends at `endAt` on the same clock, for everybody at once.
 */
import { formatTimeLeft } from '../util/format';

/** "Старт!" stays on screen this long after the start. */
export const GO_DISPLAY_MS = 900;

/** The last seconds of a race against the clock: the timer turns red and shows tenths. */
export const HURRY_MS = 10_000;

/** A beep for each of the last seconds of a race against the clock (5, 4, 3, 2, 1). */
export const FINAL_BEEPS = 5;

/**
 * Converts the engine clock (performance.now() timebase) to race time. The offset is taken
 * once per frame (`serverNow - clockNow`), so the moment of a simulation tick inside the
 * frame maps to the race time it really happened at.
 */
export function raceTimeAt(clockTime: number, serverOffset: number, startAt: number): number {
  return clockTime + serverOffset - startAt;
}

/** Race time as it goes into network messages: whole milliseconds, never negative. */
export function wireTime(raceTime: number): number {
  return Math.max(0, Math.round(raceTime));
}

/**
 * The countdown number for a race time (negative before the start): 3, 2, 1, then 0 for
 * "Старт!" during GO_DISPLAY_MS, null afterwards. More than 3 s before the start the value
 * is the number of whole seconds left (the HUD shows "get ready" for those).
 */
export function countdownValue(raceTime: number): number | null {
  if (raceTime >= GO_DISPLAY_MS) return null;
  if (raceTime >= 0) return 0;
  return Math.ceil(-raceTime / 1000);
}

/** Race timer as m:ss.d (tenths, truncated so it never shows a time not reached yet). */
export function formatRaceTimer(ms: number): string {
  const tenths = Math.max(0, Math.floor(ms / 100));
  const minutes = Math.floor(tenths / 600);
  const seconds = Math.floor(tenths / 10) % 60;
  return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}.${tenths % 10}`;
}

/**
 * The timer of a race against the clock: the time left as m:ss, the seconds rounded up (5:00
 * at the start, 0:10 ten seconds before the end), then tenths, rounded up too (0:09.9 … 0:00.1;
 * 0:00.0 once the time is up).
 */
export function formatTimerLeft(ms: number): string {
  const tenths = Math.max(0, Math.ceil(ms / 100));
  if (tenths >= 100) return formatTimeLeft(ms);
  return `0:0${Math.floor(tenths / 10)}.${tenths % 10}`;
}
